import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { message } from 'antd';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAppStore } from '../store';
import { ensureProductCategories, ensureShopOperatingCategories } from '../store/productCategories';
import { loadShopContexts } from '../store/shopContexts';
import { IPC } from '../../common/ipc/channels';
import { isElectron } from '../../common/utils/platform';
import GuideVideoModal from '../components/feature/guide-video/GuideVideoModal';
import { useGuideEntry } from './hooks/useGuideEntry';
import { useMainLayoutUpdate } from './hooks/useMainLayoutUpdate';
import { Sidebar } from './components/Sidebar';
import { MobileBottomNav } from './components/MobileBottomNav';
import { MobileTopBar } from './components/MobileTopBar';
import { LogoutModal } from './components/LogoutModal';
import { AboutModal } from './components/AboutModal';
import { UpdateModal } from './components/UpdateModal';
import { MembershipPaymentModal } from './components/MembershipPaymentModal';
import { ComputePowerDetailModal } from './components/ComputePowerDetailModal';
import { ComputePowerPurchaseModal } from './components/ComputePowerPurchaseModal';
import { ComputePowerRulesModal } from './components/ComputePowerRulesModal';
import {
  PaymentModalContext,
  type MembershipPaymentMode,
  type PaymentModalActions,
} from './paymentModalContext';
import { PAYMENT_MODAL_EVENTS } from '../utils/paymentModalEvents';
import { getSystemShopId } from '../utils/shopContext';
import './MainLayout.css';

function useMobileLayout() {
  const [isMobile, setIsMobile] = useState(() => {
    if (isElectron()) return false;
    return typeof window !== 'undefined' && window.innerWidth <= 768;
  });

  useEffect(() => {
    if (isElectron()) return;
    const mq = window.matchMedia('(max-width: 768px)');
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    setIsMobile(mq.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  return isMobile;
}

function resolvePageTransitionKey(pathname: string) {
  // 设置页内部 tab 通过子路径表达，切换 tab 时不应该触发整页卸载和淡入动画。
  if (pathname === '/settings' || pathname.startsWith('/settings/')) {
    return '/settings';
  }

  return pathname;
}

export const MainLayout: React.FC = () => {
  const isMobile = useMobileLayout();
  const {
    sidebarCollapsed,
    setSidebarCollapsed,
    clearAuthSession,
    currentShopId,
    setCurrentShopId,
    currentEcomAccountId,
    setCurrentEcomAccountId,
    setProductCategoriesOptions,
    shopContexts,
    dataSpaceState,
  } = useAppStore();
  const navigate = useNavigate();
  const location = useLocation();
  const [messageApi, contextHolder] = message.useMessage();


  // 局部 UI 状态
  const [profilePopoverOpen, setProfilePopoverOpen] = useState(false);
  const [aboutModalOpen, setAboutModalOpen] = useState(false);
  const [logoutModalOpen, setLogoutModalOpen] = useState(false);
  const [appVersionText, setAppVersionText] = useState('');
  const [guideVideoModalOpen, setGuideVideoModalOpen] = useState(false);
  const [membershipPaymentModalOpen, setMembershipPaymentModalOpen] = useState(false);
  const [membershipPaymentMode, setMembershipPaymentMode] = useState<MembershipPaymentMode>('new');
  const [membershipPaymentExpireAt, setMembershipPaymentExpireAt] = useState<string | null>(null);
  const [membershipPaymentInitialPlanCode, setMembershipPaymentInitialPlanCode] = useState<string | undefined>();
  const [computePowerDetailModalOpen, setComputePowerDetailModalOpen] = useState(false);
  const [computePowerPurchaseModalOpen, setComputePowerPurchaseModalOpen] = useState(false);
  const [computePowerRulesModalOpen, setComputePowerRulesModalOpen] = useState(false);

  // 业务 hooks
  const update = useMainLayoutUpdate(messageApi, setProfilePopoverOpen);
  const { showGuideEntry } = useGuideEntry(location.pathname);

  useEffect(() => {
    if (!window.electronAPI) {
      return;
    }

    window.electronAPI.invoke(IPC.GET_APP_VERSION)
      .then((version) => {
        if (typeof version === 'string') {
          setAppVersionText(version.trim());
        }
      })
      .catch((error) => {
        console.error('Failed to load app version.', error);
      });
  }, []);

  useEffect(() => {
    void loadShopContexts();
  }, []);

  useEffect(() => {
    const normalizedShopId = currentShopId.trim();
    if (!normalizedShopId) {
      setProductCategoriesOptions('', []);
      return;
    }

    const selectedShop = dataSpaceState.dataSpace === 'DEMO'
      ? dataSpaceState.demoWorkspace?.shop
      : shopContexts.find((item) => getSystemShopId(item) === normalizedShopId);
    const linkedAccounts = selectedShop?.douyin_accounts ?? [];
    const currentAccountStillValid = linkedAccounts.some((item) => item.ecom_account_id === currentEcomAccountId.trim());
    if (!currentAccountStillValid) {
      setCurrentEcomAccountId(linkedAccounts[0]?.ecom_account_id || '');
    }
  }, [
    currentEcomAccountId,
    currentShopId,
    dataSpaceState.dataSpace,
    dataSpaceState.demoWorkspace?.shop,
    setCurrentEcomAccountId,
    setProductCategoriesOptions,
    shopContexts,
  ]);

  useEffect(() => {
    const normalizedShopId = currentShopId.trim();
    if (!normalizedShopId) {
      return;
    }
    void ensureProductCategories({ shopId: normalizedShopId, force: true }).catch((error) => {
      console.warn('Failed to load shop-scoped product categories.', error);
    });
    void ensureShopOperatingCategories({ shopId: normalizedShopId, force: true }).catch((error) => {
      console.warn('Failed to load shop operating categories.', error);
    });
  }, [
    currentShopId,
    dataSpaceState.dataSpace,
    dataSpaceState.demoWorkspace?.shop,
    shopContexts,
  ]);

  const contentClassName = sidebarCollapsed
    ? 'main-layout__content main-layout__content--sidebar-collapsed'
    : 'main-layout__content main-layout__content--sidebar-open';
  const dataSpaceTransitionKey = [
    dataSpaceState.dataSpace,
    dataSpaceState.demoWorkspace?.scenario || '',
    dataSpaceState.demoWorkspace?.datasetId || '',
    currentShopId.trim(),
  ].join(':');
  const pageTransitionKey = `${resolvePageTransitionKey(location.pathname)}:${dataSpaceTransitionKey}`;

  const openMembershipPaymentModal = useCallback<PaymentModalActions['openMembershipPaymentModal']>((options) => {
    setMembershipPaymentMode(options?.mode || 'new');
    setMembershipPaymentExpireAt(options?.currentPlanExpireAt || null);
    setMembershipPaymentInitialPlanCode(options?.initialPlanCode);
    setMembershipPaymentModalOpen(true);
  }, []);

  const openComputePowerDetailModal = useCallback(() => {
    setComputePowerDetailModalOpen(true);
  }, []);

  const openComputePowerPurchaseModal = useCallback(() => {
    setComputePowerPurchaseModalOpen(true);
  }, []);

  const openComputePowerRulesModal = useCallback(() => {
    setComputePowerRulesModalOpen(true);
  }, []);

  const paymentModalActions = useMemo<PaymentModalActions>(() => ({
    openMembershipPaymentModal,
    openComputePowerDetailModal,
    openComputePowerPurchaseModal,
    openComputePowerRulesModal,
  }), [
    openComputePowerDetailModal,
    openComputePowerPurchaseModal,
    openComputePowerRulesModal,
    openMembershipPaymentModal,
  ]);

  useEffect(() => {
    const handleOpenMembershipPaymentModal = () => {
      openMembershipPaymentModal({ mode: 'new' });
    };
    const handleOpenComputePowerPurchaseModal = () => {
      openComputePowerPurchaseModal();
    };

    window.addEventListener(
      PAYMENT_MODAL_EVENTS.OPEN_MEMBERSHIP_PAYMENT,
      handleOpenMembershipPaymentModal,
    );
    window.addEventListener(
      PAYMENT_MODAL_EVENTS.OPEN_COMPUTE_POWER_PURCHASE,
      handleOpenComputePowerPurchaseModal,
    );
    return () => {
      window.removeEventListener(
        PAYMENT_MODAL_EVENTS.OPEN_MEMBERSHIP_PAYMENT,
        handleOpenMembershipPaymentModal,
      );
      window.removeEventListener(
        PAYMENT_MODAL_EVENTS.OPEN_COMPUTE_POWER_PURCHASE,
        handleOpenComputePowerPurchaseModal,
      );
    };
  }, [openComputePowerPurchaseModal, openMembershipPaymentModal]);

  return (
    <PaymentModalContext.Provider value={paymentModalActions}>
      <div className={`main-layout${isMobile ? ' main-layout--mobile' : ''}`}>
        {contextHolder}

        {!isMobile && (
          <Sidebar
            collapsed={sidebarCollapsed}
            location={location}
            navigate={navigate}
            currentShopId={currentShopId}
            setCurrentShopId={setCurrentShopId}
            sidebarUpdateMeta={update.sidebarUpdateMeta}
            profileUpdateMeta={update.profileUpdateMeta}
            showGuideEntry={showGuideEntry}
            setGuideVideoModalOpen={setGuideVideoModalOpen}
            setAboutModalOpen={setAboutModalOpen}
            setLogoutModalOpen={setLogoutModalOpen}
            profilePopoverOpen={profilePopoverOpen}
            setProfilePopoverOpen={setProfilePopoverOpen}
            setSidebarCollapsed={setSidebarCollapsed}
          />
        )}

        {isMobile && <MobileTopBar onLogout={() => setLogoutModalOpen(true)} />}
        {isMobile && <MobileBottomNav />}

        <main className="main-layout__main">
          <div className={contentClassName}>
            <div key={pageTransitionKey} className="main-layout__page-transition">
              <Outlet />
            </div>
          </div>
        </main>

        <LogoutModal
          open={logoutModalOpen}
          onClose={() => setLogoutModalOpen(false)}
          clearAuthSession={clearAuthSession}
          setCurrentEcomAccountId={setCurrentEcomAccountId}
          navigate={navigate}
        />
        <AboutModal
          open={aboutModalOpen}
          onClose={() => setAboutModalOpen(false)}
          appVersionText={appVersionText}
        />
        <UpdateModal
          effectiveUpdateStatus={update.effectiveUpdateStatus}
          pendingUpdateStatus={update.pendingUpdateStatus}
          updateModalOpen={update.updateModalOpen}
          setUpdateModalOpen={update.setUpdateModalOpen}
          updateActionSubmitting={update.updateActionSubmitting}
          handleUpdatePrimaryAction={update.handleUpdatePrimaryAction}
        />
        <GuideVideoModal open={guideVideoModalOpen} onClose={() => setGuideVideoModalOpen(false)} />
        <MembershipPaymentModal
          open={membershipPaymentModalOpen}
          mode={membershipPaymentMode}
          currentPlanExpireAt={membershipPaymentExpireAt}
          initialPlanCode={membershipPaymentInitialPlanCode}
          onClose={() => setMembershipPaymentModalOpen(false)}
        />
        <ComputePowerDetailModal
          open={computePowerDetailModalOpen}
          onClose={() => setComputePowerDetailModalOpen(false)}
          onBuyComputePower={openComputePowerPurchaseModal}
          onOpenRules={openComputePowerRulesModal}
        />
        <ComputePowerPurchaseModal
          open={computePowerPurchaseModalOpen}
          onClose={() => setComputePowerPurchaseModalOpen(false)}
          onOpenRules={openComputePowerRulesModal}
        />
        <ComputePowerRulesModal
          open={computePowerRulesModalOpen}
          onClose={() => setComputePowerRulesModalOpen(false)}
        />
      </div>
    </PaymentModalContext.Provider>
  );
};
